import React, { useState, useEffect } from 'react';
import { useApp } from '../App';

const GestorInventarios = () => {
  const { inventoryLocalData, setInventoryLocalData } = useApp();
  // Estados principales
  const [productos, setProductos] = useState([]);
  const [productosFiltrados, setProductosFiltrados] = useState([]);
  const [formularioVisible, setFormularioVisible] = useState(false);
  const [productoEnEdicion, setProductoEnEdicion] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [terminoBusqueda, setTerminoBusqueda] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [toast, setToast] = useState({ visible: false, mensaje: '', tipo: 'success' });
  const [productoEliminar, setProductoEliminar] = useState(null);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [paginaActual, setPaginaActual] = useState(1);
  const [cargando, setCargando] = useState(false);
  const [filtrarPocoStock, setFiltrarPocoStock] = useState(false);
  const productosPorPagina = 50;

  // Estado del formulario
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    categoria: '',
    cantidad: '',
    stock_minimo: '',
    costo: '',
    precio_lista1: '',
    precio_lista2: '',
    referencia: '',
    producto_inventariable: true,
    impuesto_cargo: '',
    incluir_iva_precio: false
  });
  const [listasPrecios, setListasPrecios] = useState([]);

  // Configuración
  const config = {
    titulo_principal: "Gestor de Inventarios",
    texto_busqueda: "Buscar productos...",
    boton_agregar: "Agregar Producto"
  };

  // Cargar datos iniciales desde Siigo
  useEffect(() => {
    cargarProductosSiigo();
    cargarCategoriasSiigo();
    cargarListasPreciosSiigo();
  }, []);
  
  const cargarListasPreciosSiigo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/price-lists`);
      const data = await response.json();
      if (data.success && data.price_lists) {
        setListasPrecios(data.price_lists);
      }
    } catch (error) {
      console.error('Error cargando listas de precios:', error);
    }
  };

  const cargarCategoriasSiigo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/account-groups`);
      const data = await response.json();
      
      if (data.success && data.groups) {
        setCategorias(data.groups);
      }
    } catch (error) {
      console.error('Error cargando categorías de Siigo:', error);
    }
  };

  const cargarProductosSiigo = async (forzarRecarga = false) => {
    setCargando(true);
    try {
      // Usar datos locales precargados del contexto (bootstrap)
      let datosLocales = inventoryLocalData || {};
      
      // Si no hay datos precargados, cargar desde API como fallback
      if (Object.keys(datosLocales).length === 0) {
        try {
          const localDataResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/local-data`);
          const localDataResult = await localDataResponse.json();
          datosLocales = localDataResult.success ? localDataResult.data : {};
        } catch (localError) {
          console.error('Error cargando datos locales:', localError);
        }
      }

      // Intentar cargar desde cache si existe y no es forzada la recarga
      if (!forzarRecarga) {
        const cachedProducts = localStorage.getItem('siigo_productos_cache');
        const cachedTimestamp = localStorage.getItem('siigo_sync_timestamp');
        
        if (cachedProducts && cachedTimestamp) {
          const cacheAge = Date.now() - new Date(cachedTimestamp).getTime();
          // Si el cache tiene menos de 10 minutos (600000 ms), usarlo
          if (cacheAge < 600000) {
            console.log('✅ Cargando productos desde cache de sincronización automática');
            let productosCache = JSON.parse(cachedProducts);
            
            // IMPORTANTE: Actualizar datos locales (costo y stock_minimo) desde MongoDB
            productosCache = productosCache.map(p => {
              const datosProducto = datosLocales[p.id] || datosLocales[p.codigo] || datosLocales[p.code] || {};
              return {
                ...p,
                costo: datosProducto.costo !== undefined ? datosProducto.costo : (p.costo || 0),
                stock_minimo: datosProducto.stock_minimo !== undefined ? datosProducto.stock_minimo : (p.stock_minimo || 0)
              };
            });
            
            setProductos(productosCache);
            setProductosFiltrados(productosCache);
            mostrarToast(`✓ ${productosCache.length} productos cargados (cache)`, 'success');
            setCargando(false);
            return;
          }
        }
      }
      
      // Si no hay cache o está desactualizado, cargar desde API
      console.log('🔄 Cargando productos directamente desde Siigo API...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/products`);
      const data = await response.json();
      
      if (data.success && data.products) {
        const productosTransformados = data.products.map(p => {
          // Mapear impuesto desde Siigo
          let impuestoNombre = 'No aplica';
          if (p.taxes && p.taxes.length > 0) {
            const taxId = p.taxes[0].id;
            if (taxId === 5848) impuestoNombre = 'IVA 19%';
            else if (taxId === 5849) impuestoNombre = 'IVA 5%';
            else if (taxId === 13939) impuestoNombre = 'IVA 0%';
            else impuestoNombre = p.taxes[0].name || 'No aplica';
          }
          
          // Encontrar precios por posición
          const priceList = p.prices?.[0]?.price_list || [];
          const efectivo = priceList.find(pl => pl.position === 1)?.value || 0;
          const tarjeta = priceList.find(pl => pl.position === 2)?.value || 0;
          
          // Obtener datos locales de este producto (buscar por ID y por codigo)
          const datosProducto = datosLocales[p.id] || datosLocales[p.code] || { costo: 0, stock_minimo: 0, updated_at: null };
          
          return {
            id: p.id,
            codigo: p.code || '',
            nombre: p.name || '',
            categoria: p.account_group?.name || 'Sin categoría',
            categoriaId: p.account_group?.id || null,
            cantidad: p.available_quantity || 0,
            stock_minimo: datosProducto.stock_minimo || 0,  // Stock mínimo desde MongoDB
            costo: datosProducto.costo || 0,  // Costo desde MongoDB
            precio_venta: efectivo || tarjeta || 0,  // Precio de venta = EFECTIVO o TARJETA
            precio_lista1: efectivo,  // EFECTIVO (posición 1)
            precio_lista2: tarjeta,   // TARJETA (posición 2)
            referencia: p.reference || '',
            producto_inventariable: p.stock_control !== false,
            impuesto_cargo: impuestoNombre,
            incluir_iva_precio: p.tax_included || false,
            porcentaje_ganancia: 0,
            fecha_creacion: datosProducto.updated_at || p.metadata?.created || p.created_at || null,
            active: p.active !== false,  // Estado activo del producto en Siigo
            warehouses: p.warehouses || []  // Stock por bodega desde Siigo
          };
        });
        
        setProductos(productosTransformados);
        setProductosFiltrados(productosTransformados);
        
        // Mostrar resumen
        console.log(`✓ ${productosTransformados.length} productos cargados de Siigo`);
        const categorias = {};
        productosTransformados.forEach(p => {
          categorias[p.categoria] = (categorias[p.categoria] || 0) + 1;
        });
        console.log('Categorías cargadas:', categorias);
        
        mostrarToast(`✓ ${productosTransformados.length} productos cargados`, 'success');
      }
    } catch (error) {
      console.error('Error cargando productos de Siigo:', error);
      mostrarToast('Error al cargar productos de Siigo', 'error');
    } finally {
      setCargando(false);
    }
  };

  // Filtrar productos cuando cambie el término de búsqueda o filtro de poco stock
  // Estado para rastrear si el cambio en productos fue por edición (no debe resetear página)
  const [ultimaEdicion, setUltimaEdicion] = useState(false);

  useEffect(() => {
    // Solo resetear página cuando cambia la búsqueda o filtro, NO cuando se edita un producto
    if (!ultimaEdicion) {
      setPaginaActual(1);
    }
    setUltimaEdicion(false); // Reset flag
    
    let filtrados = productos;
    
    // Aplicar filtro de búsqueda
    if (terminoBusqueda.trim() !== '') {
      const termino = terminoBusqueda.toLowerCase();
      filtrados = filtrados.filter(p =>
        p.codigo.toLowerCase().includes(termino) ||
        p.nombre.toLowerCase().includes(termino) ||
        (p.categoria && p.categoria.toLowerCase().includes(termino))
      );
    }
    
    // Aplicar filtro de poco stock
    if (filtrarPocoStock) {
      filtrados = filtrados.filter(p => p.stock_minimo > 0 && p.cantidad <= p.stock_minimo);
    }
    
    setProductosFiltrados(filtrados);
  }, [terminoBusqueda, productos, filtrarPocoStock]);

  // Funciones de utilidad
  const mostrarToast = (mensaje, tipo = 'success') => {
    setToast({ visible: true, mensaje, tipo });
    setTimeout(() => {
      setToast({ visible: false, mensaje: '', tipo: 'success' });
    }, 3000);
  };

  const calcularPorcentajeGanancia = (costo, precioVenta) => {
    if (costo === 0) return 0;
    return ((precioVenta - costo) / costo * 100);
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD'
    }).format(valor);
  };

  const formatearPorcentaje = (valor) => {
    return `${valor.toFixed(1)}%`;
  };

  // Función para guardar SOLO datos locales (costo y stock mínimo) sin enviar a Siigo
  const guardarSoloDatosLocales = async () => {
    if (!productoEnEdicion) {
      mostrarToast('No hay producto en edición', 'error');
      return;
    }

    try {
      setCargando(true);
      const localDataResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/local-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productoEnEdicion.id,
          product_code: productoEnEdicion.codigo || productoEnEdicion.code || '',
          costo: parseFloat(formData.costo) || 0,
          stock_minimo: parseInt(formData.stock_minimo) || 0
        })
      });
      
      const localDataResult = await localDataResponse.json();
      
      if (localDataResult.success) {
        mostrarToast('¡Costo y stock mínimo guardados!');
        
        const newCosto = parseFloat(formData.costo) || 0;
        const newStockMinimo = parseInt(formData.stock_minimo) || 0;
        const productCode = productoEnEdicion.codigo || productoEnEdicion.code || '';
        
        // Actualizar el contexto global para persistir entre navegaciones
        setInventoryLocalData(prev => {
          const updated = { ...prev };
          const entry = { costo: newCosto, stock_minimo: newStockMinimo, updated_at: new Date().toISOString() };
          updated[productoEnEdicion.id] = entry;
          if (productCode) updated[productCode] = entry;
          return updated;
        });
        
        // Actualizar el producto en el estado local
        const productoActualizado = {
          ...productoEnEdicion,
          costo: newCosto,
          stock_minimo: newStockMinimo
        };
        
        // Marcar que es una edición para NO resetear la página
        setUltimaEdicion(true);
        
        setProductos(prev => prev.map(p => 
          p.id === productoEnEdicion.id ? productoActualizado : p
        ));
        setProductosFiltrados(prev => prev.map(p => 
          p.id === productoEnEdicion.id ? productoActualizado : p
        ));
        
        // Cerrar formulario y limpiar estado
        setFormularioVisible(false);
        setProductoEnEdicion(null);
      } else {
        mostrarToast('Error al guardar datos locales: ' + (localDataResult.error || 'Error desconocido'), 'error');
      }
    } catch (error) {
      console.error('Error guardando datos locales:', error);
      mostrarToast('Error de conexión al guardar', 'error');
    } finally {
      setCargando(false);
    }
  };

  // Funciones de manejo de productos
  const agregarProducto = async () => {
    // Validaciones
    if (!formData.codigo.trim() || !formData.nombre.trim()) {
      mostrarToast('Código y nombre son obligatorios', 'error');
      return;
    }

    // Si estamos editando y no hay categoría seleccionada pero el producto ya tiene una, usarla
    let categoriaParaEnviar = categoriaSeleccionada;
    if (!categoriaParaEnviar && productoEnEdicion?.categoriaId) {
      categoriaParaEnviar = { id: productoEnEdicion.categoriaId, name: productoEnEdicion.categoria };
      console.log('⚠️ Usando categoría del producto existente:', categoriaParaEnviar);
    }

    if (!categoriaParaEnviar) {
      mostrarToast('Debe seleccionar una categoría de Siigo', 'error');
      return;
    }

    setCargando(true);
    try {
      console.log('🔍 DEBUG - productoEnEdicion:', productoEnEdicion);
      console.log('🔍 DEBUG - formData:', formData);
      console.log('🔍 DEBUG - categoriaParaEnviar:', categoriaParaEnviar);
      
      const productoSiigo = {
        code: formData.codigo.trim(),
        name: formData.nombre.trim(),
        precio_lista1: parseFloat(formData.precio_lista1) || 0,  // EFECTIVO (posición 1)
        precio_lista2: parseFloat(formData.precio_lista2) || 0,  // TARJETA (posición 2)
        referencia: formData.referencia,
        producto_inventariable: formData.producto_inventariable,
        account_group_id: categoriaParaEnviar.id,
        tax: formData.impuesto_cargo,
        tax_included: formData.incluir_iva_precio
      };

      const url = productoEnEdicion 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/siigo/products/${productoEnEdicion.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/siigo/products`;
      
      const method = productoEnEdicion ? 'PUT' : 'POST';
      
      console.log('📤 Enviando a Siigo:', { url, method, productoSiigo });

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productoSiigo)
      });

      console.log('📥 Respuesta del servidor:', response.status, response.statusText);
      
      // Verificar status code antes de procesar
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error HTTP:', response.status, errorText);
        mostrarToast(`Error ${response.status}: No se pudo conectar con Siigo. Verifica la conexión.`, 'error');
        setCargando(false);
        return;
      }
      
      const data = await response.json();
      console.log('📥 Data recibida:', data);

      if (data.success) {
        // Guardar datos locales en MongoDB (costo y stock_minimo)
        const productoId = productoEnEdicion?.id || data.product?.id;
        if (productoId) {
          try {
            const localDataResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/local-data`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                product_id: productoId,
                product_code: productoEnEdicion?.codigo || productoEnEdicion?.code || formData.codigo || '',
                costo: parseFloat(formData.costo) || 0,
                stock_minimo: parseInt(formData.stock_minimo) || 0
              })
            });
            
            const localDataResult = await localDataResponse.json();
            if (localDataResult.success) {
              // Actualizar contexto global
              const pCode = productoEnEdicion?.codigo || productoEnEdicion?.code || formData.codigo || '';
              setInventoryLocalData(prev => {
                const updated = { ...prev };
                const entry = { costo: parseFloat(formData.costo) || 0, stock_minimo: parseInt(formData.stock_minimo) || 0, updated_at: new Date().toISOString() };
                updated[productoId] = entry;
                if (pCode) updated[pCode] = entry;
                return updated;
              });
            } else {
              console.error('Error al guardar datos locales:', localDataResult.error);
            }
          } catch (localError) {
            console.error('Error guardando datos locales:', localError);
          }
        }
        
        mostrarToast(productoEnEdicion ? '¡Producto actualizado!' : '¡Producto creado!');
        
        // ⚡ OPTIMIZACIÓN: Actualizar SOLO el producto editado en el estado local (sin recargar todo)
        if (productoEnEdicion) {
          // Actualizar el producto existente en el estado
          const productoActualizado = {
            ...productoEnEdicion,
            codigo: formData.codigo.trim(),
            nombre: formData.nombre.trim(),
            precio_efectivo: parseFloat(formData.precio_lista1) || 0,
            precio_tarjeta: parseFloat(formData.precio_lista2) || 0,
            referencia: formData.referencia,
            costo: parseFloat(formData.costo) || 0,
            stock_minimo: parseInt(formData.stock_minimo) || 0,
            categoria: categoriaSeleccionada?.name || productoEnEdicion.categoria,
            impuesto: formData.impuesto_cargo === 5848 ? 'IVA 19%' : 
                      formData.impuesto_cargo === 5849 ? 'IVA 5%' : 
                      formData.impuesto_cargo === 13939 ? 'IVA 0%' : 'No aplica'
          };
          
          // Marcar que es una edición para NO resetear la página
          setUltimaEdicion(true);
          
          setProductos(prev => prev.map(p => 
            p.id === productoEnEdicion.id ? productoActualizado : p
          ));
          setProductosFiltrados(prev => prev.map(p => 
            p.id === productoEnEdicion.id ? productoActualizado : p
          ));
          
          // Invalidar cache para que la próxima carga completa traiga datos frescos
          localStorage.removeItem('siigo_productos_cache');
          localStorage.removeItem('siigo_sync_timestamp');
          
          console.log('⚡ Producto actualizado localmente (sin recargar inventario completo)');
        } else {
          // Producto nuevo: agregar al estado local
          const nuevoProducto = {
            id: data.product?.id || Date.now().toString(),
            codigo: formData.codigo.trim(),
            nombre: formData.nombre.trim(),
            precio_efectivo: parseFloat(formData.precio_lista1) || 0,
            precio_tarjeta: parseFloat(formData.precio_lista2) || 0,
            referencia: formData.referencia,
            costo: parseFloat(formData.costo) || 0,
            stock_minimo: parseInt(formData.stock_minimo) || 0,
            categoria: categoriaSeleccionada?.name || 'Sin categoría',
            impuesto: formData.impuesto_cargo === 5848 ? 'IVA 19%' : 
                      formData.impuesto_cargo === 5849 ? 'IVA 5%' : 
                      formData.impuesto_cargo === 13939 ? 'IVA 0%' : 'No aplica',
            activo: true,
            fecha_creacion: new Date().toISOString()
          };
          
          setProductos(prev => [nuevoProducto, ...prev]);
          setProductosFiltrados(prev => [nuevoProducto, ...prev]);
          
          // Invalidar cache
          localStorage.removeItem('siigo_productos_cache');
          localStorage.removeItem('siigo_sync_timestamp');
          
          console.log('⚡ Producto nuevo agregado localmente');
        }
        
        // Limpiar formulario
        resetFormulario();
        setFormularioVisible(false);
      } else {
        mostrarToast('Error: ' + (data.error || 'No se pudo guardar en Siigo'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      mostrarToast('Error de conexión con Siigo', 'error');
    } finally {
      setCargando(false);
    }
  };

  const editarProducto = (id) => {
    const producto = productos.find(p => p.id === id);
    if (!producto) return;

    setProductoEnEdicion(producto);
    setFormData({
      codigo: producto.codigo,
      nombre: producto.nombre,
      categoria: producto.categoria,
      cantidad: producto.cantidad.toString(),
      stock_minimo: producto.stock_minimo?.toString() || '',
      costo: producto.costo.toString(),
      precio_lista1: producto.precio_lista1?.toString() || '',
      precio_lista2: producto.precio_lista2?.toString() || '',
      referencia: producto.referencia || '',
      producto_inventariable: producto.producto_inventariable !== false,
      impuesto_cargo: producto.impuesto_cargo,
      incluir_iva_precio: producto.incluir_iva_precio
    });
    
    // Buscar la categoría seleccionada
    const catEncontrada = categorias.find(c => c.id === producto.categoriaId);
    if (catEncontrada) {
      setCategoriaSeleccionada(catEncontrada);
    }
    
    setFormularioVisible(true);
  };

  const eliminarProducto = async (id) => {
    try {
      // Nota: Siigo API no permite eliminar productos fácilmente
      // Por ahora solo removemos localmente y mostramos advertencia
      const productosActualizados = productos.filter(p => p.id !== id);
      setProductos(productosActualizados);
      setProductoEliminar(null);
      mostrarToast('Producto removido de la vista (Nota: Para eliminar en Siigo debe hacerse desde su plataforma)', 'error');
    } catch (error) {
      console.error('Error:', error);
      mostrarToast('Error al eliminar producto', 'error');
    }
  };

  const resetFormulario = () => {
    setFormData({
      codigo: '',
      nombre: '',
      categoria: '',
      cantidad: '',
      stock_minimo: '',
      costo: '',
      precio_lista1: '',
      precio_lista2: '',
      referencia: '',
      producto_inventariable: true,
      impuesto_cargo: '',
      incluir_iva_precio: false
    });
    setProductoEnEdicion(null);
    setCategoriaSeleccionada(null);
  };

  const toggleFormulario = () => {
    if (formularioVisible) {
      resetFormulario();
    }
    setFormularioVisible(!formularioVisible);
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    agregarProducto();
  };

  // Paginación
  const indexUltimo = paginaActual * productosPorPagina;
  const indexPrimero = indexUltimo - productosPorPagina;
  const productosPaginados = productosFiltrados.slice(indexPrimero, indexUltimo);
  const totalPaginas = Math.ceil(productosFiltrados.length / productosPorPagina);

  // Calcular estadísticas
  const totalProductos = productos.length;
  const valorTotal = productos.reduce((sum, p) => sum + (p.precio_venta * p.cantidad), 0);
  const gananciaPotencial = productos.reduce((sum, p) => sum + ((p.precio_venta - p.costo) * p.cantidad), 0);
  const productosBajoStock = productos.filter(p => p.stock_minimo > 0 && p.cantidad <= p.stock_minimo).length;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      {/* Toast */}
      {toast.visible && (
        <div className={`fixed top-5 right-5 px-6 py-3 rounded-lg text-white font-medium z-50 shadow-lg ${
          toast.tipo === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}>
          {toast.mensaje}
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            {config.titulo_principal}
          </h1>
          <p className="text-slate-600">Administra tu inventario de productos de manera eficiente</p>
        </header>

        {/* Controles superiores */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Buscador */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder={config.texto_busqueda}
                  value={terminoBusqueda}
                  onChange={(e) => setTerminoBusqueda(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              {filtrarPocoStock && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    Mostrando solo productos con poco stock
                  </span>
                  <button
                    onClick={() => setFiltrarPocoStock(false)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Quitar filtro
                  </button>
                </div>
              )}
            </div>

            {/* Botones */}
            <div className="flex gap-2">
              <button
                onClick={() => cargarProductosSiigo(true)}
                disabled={cargando}
                className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <svg className={`h-5 w-5 ${cargando ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{cargando ? 'Sincronizando...' : 'Sincronizar con Siigo'}</span>
              </button>
              <button
                onClick={toggleFormulario}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>{config.boton_agregar}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Formulario */}
        {formularioVisible && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">
              {productoEnEdicion ? 'Editar Producto' : 'Nuevo Producto'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Código</label>
                <input
                  type="text"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">Categoría del Producto (Siigo)</label>
                <input
                  type="text"
                  value={categoriaSeleccionada ? categoriaSeleccionada.name : formData.categoria}
                  onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  onFocus={() => setDropdownVisible(true)}
                  onBlur={() => setTimeout(() => setDropdownVisible(false), 200)}
                  placeholder="Seleccionar categoría de Siigo..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
                {dropdownVisible && categorias.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-slate-300 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                    {categorias
                      .filter(cat => cat.name.toLowerCase().includes(formData.categoria.toLowerCase()))
                      .map((cat) => (
                        <div
                          key={cat.id}
                          onClick={() => {
                            setCategoriaSeleccionada(cat);
                            setFormData({ ...formData, categoria: cat.name });
                            setDropdownVisible(false);
                          }}
                          className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm border-b border-slate-100"
                        >
                          <div className="font-medium">{cat.name}</div>
                          <div className="text-xs text-slate-500">ID: {cat.id}</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cantidad</label>
                <input
                  type="number"
                  value={formData.cantidad}
                  onChange={(e) => setFormData({ ...formData, cantidad: e.target.value })}
                  min="0"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Stock Mínimo</label>
                <input
                  type="number"
                  value={formData.stock_minimo}
                  onChange={(e) => setFormData({ ...formData, stock_minimo: e.target.value })}
                  min="0"
                  placeholder="Cantidad mínima en inventario"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Costo ($)</label>
                <input
                  type="number"
                  value={formData.costo}
                  onChange={(e) => setFormData({ ...formData, costo: e.target.value })}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {listasPrecios.find(l => l.position === 1)?.name || 'EFECTIVO'} ($)
                </label>
                <input
                  type="number"
                  value={formData.precio_lista1}
                  onChange={(e) => setFormData({ ...formData, precio_lista1: e.target.value })}
                  min="0"
                  step="0.01"
                  placeholder="Precio EFECTIVO"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {listasPrecios.find(l => l.position === 2)?.name || 'TARJETA'} ($)
                </label>
                <input
                  type="number"
                  value={formData.precio_lista2}
                  onChange={(e) => setFormData({ ...formData, precio_lista2: e.target.value })}
                  min="0"
                  step="0.01"
                  placeholder="Precio TARJETA"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Referencia</label>
                <input
                  type="text"
                  value={formData.referencia}
                  onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                  placeholder="Referencia del producto"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div className="flex items-center gap-3 p-4 bg-white/60 rounded-xl border-2 border-transparent hover:border-slate-200 transition-all">
                <input
                  id="producto_inventariable"
                  type="checkbox"
                  checked={formData.producto_inventariable}
                  onChange={(e) => setFormData({ ...formData, producto_inventariable: e.target.checked })}
                  className="w-5 h-5 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                />
                <label htmlFor="producto_inventariable" className="font-medium cursor-pointer">
                  Producto Inventariable
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Impuesto Cargo</label>
                <select
                  value={formData.impuesto_cargo}
                  onChange={(e) => setFormData({ ...formData, impuesto_cargo: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">Seleccionar impuesto</option>
                  <option value="IVA 19%">IVA 19%</option>
                  <option value="IVA 5%">IVA 5%</option>
                  <option value="Impoconsumo 8%">Impoconsumo 8%</option>
                  <option value="Impoconsumo por valor">Impoconsumo por valor</option>
                  <option value="IVA 0%">IVA 0%</option>
                  <option value="No aplica">No aplica</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  id="incluir_iva"
                  type="checkbox"
                  checked={formData.incluir_iva_precio}
                  onChange={(e) => setFormData({ ...formData, incluir_iva_precio: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                />
                <label htmlFor="incluir_iva" className="ml-3 text-sm font-medium text-slate-700">
                  Incluir IVA en el precio de venta
                </label>
              </div>

              <div className="md:col-span-2 lg:col-span-3 flex items-end gap-2 flex-wrap">
                <button
                  onClick={handleSubmit}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  {productoEnEdicion ? 'Actualizar Todo en Siigo' : 'Guardar'}
                </button>
                {productoEnEdicion && (
                  <button
                    onClick={guardarSoloDatosLocales}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                    title="Guarda solo Costo y Stock Mínimo sin modificar Siigo"
                  >
                    💾 Solo Costo y Stock
                  </button>
                )}
                <button
                  onClick={toggleFormulario}
                  className="bg-slate-500 hover:bg-slate-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Total Productos</p>
                <p className="text-2xl font-bold text-slate-900">{totalProductos}</p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setFiltrarPocoStock(!filtrarPocoStock)}
            className={`bg-white rounded-lg shadow-sm border p-6 text-left transition-all hover:shadow-md ${
              filtrarPocoStock ? 'border-red-500 ring-2 ring-red-200' : 'border-slate-200'
            }`}
          >
            <div className="flex items-center">
              <div className={`p-2 rounded-lg ${filtrarPocoStock ? 'bg-red-200' : 'bg-red-100'}`}>
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  Poco Stock
                  {filtrarPocoStock && (
                    <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full">Filtrado</span>
                  )}
                </p>
                <p className="text-2xl font-bold text-red-600">{productosBajoStock}</p>
              </div>
            </div>
          </button>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Valor Total Inventario</p>
                <p className="text-2xl font-bold text-slate-900">{formatearMoneda(valorTotal)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Ganancia Potencial</p>
                <p className="text-2xl font-bold text-slate-900">{formatearMoneda(gananciaPotencial)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de productos */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800">Lista de Productos</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cantidad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Costo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Precio Venta</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">% Ganancia</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Última Edición</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {cargando ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-lg font-medium text-slate-700">Cargando inventario...</p>
                    </td>
                  </tr>
                ) : productosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                      <svg className="mx-auto h-12 w-12 text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <p className="text-lg font-medium">No hay productos en el inventario</p>
                      <p className="text-sm">Agrega tu primer producto para comenzar</p>
                    </td>
                  </tr>
                ) : (
                  productosPaginados.map((producto) => {
                    const porcentajeGanancia = calcularPorcentajeGanancia(producto.costo, producto.precio_venta);
                    const colorGanancia = porcentajeGanancia >= 0 ? 'text-green-600' : 'text-red-600';

                    return (
                      <tr key={producto.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{producto.codigo}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{producto.nombre}</td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex flex-col">
                            <span className={`font-medium ${producto.stock_minimo > 0 && producto.cantidad <= producto.stock_minimo ? 'text-red-600' : 'text-slate-900'}`}>
                              {producto.cantidad}
                            </span>
                            {producto.stock_minimo > 0 && producto.cantidad <= producto.stock_minimo && (
                              <span className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Poco Stock
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{formatearMoneda(producto.costo)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{formatearMoneda(producto.precio_venta)}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${colorGanancia}`}>
                          {formatearPorcentaje(porcentajeGanancia)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {producto.fecha_creacion ? new Date(producto.fecha_creacion).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {productoEliminar === producto.id ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => eliminarProducto(producto.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                              >
                                Confirmar
                              </button>
                              <button
                                onClick={() => setProductoEliminar(null)}
                                className="bg-slate-500 hover:bg-slate-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => editarProducto(producto.id)}
                                className="text-blue-600 hover:text-blue-900 transition-colors"
                                title="Editar producto"
                              >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => setProductoEliminar(producto.id)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title="Eliminar producto"
                              >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Paginación */}
          {!cargando && productosFiltrados.length > productosPorPagina && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Mostrando {indexPrimero + 1} - {Math.min(indexUltimo, productosFiltrados.length)} de {productosFiltrados.length} productos
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPaginaActual(Math.max(1, paginaActual - 1))}
                  disabled={paginaActual === 1}
                  className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="px-3 py-1 text-sm text-slate-600">
                  Página {paginaActual} de {totalPaginas}
                </span>
                <button
                  onClick={() => setPaginaActual(Math.min(totalPaginas, paginaActual + 1))}
                  disabled={paginaActual === totalPaginas}
                  className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GestorInventarios;
